import {
  ENERGY_SOURCES,
  formatSig,
  reviewed,
  roundTo,
  type AnswerCheck,
  type DataSource,
  type EnergyDirection,
  type ReviewStatus,
} from './energyCalorimetry';

// ---------------------------------------------------------------------------
// Senior: energy profile diagrams
// ---------------------------------------------------------------------------

export interface ProfileReaction {
  id: string;
  name: string;
  equation: string;
  deltaHkJ: number;
  sources: DataSource[];
  reviewStatus: ReviewStatus;
}

export const PROFILE_REACTIONS: ProfileReaction[] = [
  {
    id: 'methane-combustion',
    name: 'Combustion of methane',
    equation: 'CH₄(g) + 2O₂(g) → CO₂(g) + 2H₂O(l)',
    deltaHkJ: -890,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'haber',
    name: 'Ammonia synthesis',
    equation: 'N₂(g) + 3H₂(g) → 2NH₃(g)',
    deltaHkJ: -92,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'neutralisation',
    name: 'Strong acid–strong base neutralisation',
    equation: 'H⁺(aq) + OH⁻(aq) → H₂O(l)',
    deltaHkJ: -55.8,
    ...reviewed([ENERGY_SOURCES.derived]),
  },
  {
    id: 'limestone',
    name: 'Decomposition of calcium carbonate',
    equation: 'CaCO₃(s) → CaO(s) + CO₂(g)',
    deltaHkJ: 178,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'photosynthesis',
    name: 'Photosynthesis (overall)',
    equation: '6CO₂(g) + 6H₂O(l) → C₆H₁₂O₆(s) + 6O₂(g)',
    deltaHkJ: 2803,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'hydrogen-combustion',
    name: 'Combustion of hydrogen',
    equation: '2H₂(g) + O₂(g) → 2H₂O(l)',
    deltaHkJ: -571.6,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'ethanol-combustion',
    name: 'Combustion of ethanol',
    equation: 'C₂H₅OH(l) + 3O₂(g) → 2CO₂(g) + 3H₂O(l)',
    deltaHkJ: -1367,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'nitrogen-monoxide',
    name: 'Nitrogen monoxide from air',
    equation: 'N₂(g) + O₂(g) → 2NO(g)',
    deltaHkJ: 180.6,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'water-gas',
    name: 'Water–gas reaction',
    equation: 'C(s) + H₂O(g) → CO(g) + H₂(g)',
    deltaHkJ: 131.3,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
];

/** Catalyst lowers the illustrative Ea to this fraction. Ea values are not reviewed data. */
export const CATALYST_EA_FRACTION = 0.6;

export interface EnergyProfile {
  reactantsLevel: number;
  productsLevel: number;
  peakLevel: number;
  catalysedPeakLevel: number;
  forwardEa: number;
  catalysedEa: number;
  reverseEa: number;
  deltaH: number;
  direction: EnergyDirection;
}

/**
 * Builds an energy profile. Reactants sit at 0 kJ. Returns null (fail closed) when Ea is not
 * positive or cannot reach the products of an endothermic reaction.
 */
export function buildEnergyProfile(deltaHkJ: number, activationEnergykJ: number): EnergyProfile | null {
  if (!Number.isFinite(deltaHkJ) || deltaHkJ === 0) return null;
  if (!Number.isFinite(activationEnergykJ) || activationEnergykJ <= 0) return null;
  if (activationEnergykJ <= Math.max(0, deltaHkJ)) return null;
  const catalysedEa = Math.max(activationEnergykJ * CATALYST_EA_FRACTION, Math.max(0, deltaHkJ) + 1);
  return {
    reactantsLevel: 0,
    productsLevel: deltaHkJ,
    peakLevel: activationEnergykJ,
    catalysedPeakLevel: catalysedEa,
    forwardEa: activationEnergykJ,
    catalysedEa,
    reverseEa: activationEnergykJ - deltaHkJ,
    deltaH: deltaHkJ,
    direction: deltaHkJ < 0 ? 'exothermic' : 'endothermic',
  };
}

export type CatalystPrediction = 'lower' | 'same' | 'higher';

export function checkCatalystPrediction(prediction: CatalystPrediction): AnswerCheck {
  if (prediction === 'same') {
    return {
      verdict: 'correct',
      feedback:
        'Correct. A catalyst gives a pathway with a lower Ea, but reactants and products are unchanged, so ΔH is the same.',
    };
  }
  return {
    verdict: 'other',
    feedback:
      prediction === 'lower'
        ? 'Only the peak moves. The catalyst lowers Ea, but the reactant and product levels stay put, so ΔH does not change.'
        : 'A catalyst never raises ΔH. It lowers the activation-energy peak; the start and end levels are fixed by the substances.',
  };
}

// ---------------------------------------------------------------------------
// Senior: Hess's law
// ---------------------------------------------------------------------------

/** Species → coefficient. Products positive, reactants negative. */
export type SpeciesVector = Record<string, number>;

export interface HessStep {
  equation: string;
  species: SpeciesVector;
  deltaHkJ: number;
}

export interface HessSet {
  id: string;
  name: string;
  target: string;
  targetSpecies: SpeciesVector;
  steps: HessStep[];
  /** Multipliers that reach the target, in step order. */
  solution: number[];
  /** Accepted textbook value for comparison, kJ mol⁻¹. */
  referenceDeltaHkJ: number;
  sources: DataSource[];
  reviewStatus: ReviewStatus;
}

const C_COMBUSTION: HessStep = {
  equation: 'C(s) + O₂(g) → CO₂(g)',
  species: { 'C(s)': -1, 'O₂(g)': -1, 'CO₂(g)': 1 },
  deltaHkJ: -393.5,
};
const H2_COMBUSTION: HessStep = {
  equation: 'H₂(g) + ½O₂(g) → H₂O(l)',
  species: { 'H₂(g)': -1, 'O₂(g)': -0.5, 'H₂O(l)': 1 },
  deltaHkJ: -285.8,
};

const ETHANE_COMBUSTION: HessStep = {
  equation: 'C₂H₆(g) + 3½O₂(g) → 2CO₂(g) + 3H₂O(l)',
  species: { 'C₂H₆(g)': -1, 'O₂(g)': -3.5, 'CO₂(g)': 2, 'H₂O(l)': 3 },
  deltaHkJ: -1560,
};

export const HESS_SETS: HessSet[] = [
  {
    id: 'methane-formation',
    name: 'Formation of methane',
    target: 'C(s) + 2H₂(g) → CH₄(g)',
    targetSpecies: { 'C(s)': -1, 'H₂(g)': -2, 'CH₄(g)': 1 },
    steps: [
      C_COMBUSTION,
      H2_COMBUSTION,
      {
        equation: 'CH₄(g) + 2O₂(g) → CO₂(g) + 2H₂O(l)',
        species: { 'CH₄(g)': -1, 'O₂(g)': -2, 'CO₂(g)': 1, 'H₂O(l)': 2 },
        deltaHkJ: -890.3,
      },
    ],
    solution: [1, 2, -1],
    referenceDeltaHkJ: -74.8,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'carbon-monoxide-formation',
    name: 'Formation of carbon monoxide',
    target: 'C(s) + ½O₂(g) → CO(g)',
    targetSpecies: { 'C(s)': -1, 'O₂(g)': -0.5, 'CO(g)': 1 },
    steps: [
      C_COMBUSTION,
      {
        equation: 'CO(g) + ½O₂(g) → CO₂(g)',
        species: { 'CO(g)': -1, 'O₂(g)': -0.5, 'CO₂(g)': 1 },
        deltaHkJ: -283.0,
      },
    ],
    solution: [1, -1],
    referenceDeltaHkJ: -110.5,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'ethanol-formation',
    name: 'Formation of ethanol',
    target: '2C(s) + 3H₂(g) + ½O₂(g) → C₂H₅OH(l)',
    targetSpecies: { 'C(s)': -2, 'H₂(g)': -3, 'O₂(g)': -0.5, 'C₂H₅OH(l)': 1 },
    steps: [
      C_COMBUSTION,
      H2_COMBUSTION,
      {
        equation: 'C₂H₅OH(l) + 3O₂(g) → 2CO₂(g) + 3H₂O(l)',
        species: { 'C₂H₅OH(l)': -1, 'O₂(g)': -3, 'CO₂(g)': 2, 'H₂O(l)': 3 },
        deltaHkJ: -1367.3,
      },
    ],
    solution: [2, 3, -1],
    referenceDeltaHkJ: -277.1,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'ethene-hydrogenation',
    name: 'Hydrogenation of ethene',
    target: 'C₂H₄(g) + H₂(g) → C₂H₆(g)',
    targetSpecies: { 'C₂H₄(g)': -1, 'H₂(g)': -1, 'C₂H₆(g)': 1 },
    steps: [
      {
        equation: 'C₂H₄(g) + 3O₂(g) → 2CO₂(g) + 2H₂O(l)',
        species: { 'C₂H₄(g)': -1, 'O₂(g)': -3, 'CO₂(g)': 2, 'H₂O(l)': 2 },
        deltaHkJ: -1411,
      },
      H2_COMBUSTION,
      ETHANE_COMBUSTION,
    ],
    solution: [1, 1, -1],
    referenceDeltaHkJ: -137,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'propane-formation',
    name: 'Formation of propane',
    target: '3C(s) + 4H₂(g) → C₃H₈(g)',
    targetSpecies: { 'C(s)': -3, 'H₂(g)': -4, 'C₃H₈(g)': 1 },
    steps: [
      C_COMBUSTION,
      H2_COMBUSTION,
      {
        equation: 'C₃H₈(g) + 5O₂(g) → 3CO₂(g) + 4H₂O(l)',
        species: { 'C₃H₈(g)': -1, 'O₂(g)': -5, 'CO₂(g)': 3, 'H₂O(l)': 4 },
        deltaHkJ: -2219,
      },
    ],
    solution: [3, 4, -1],
    referenceDeltaHkJ: -104.7,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'ethane-formation',
    name: 'Formation of ethane',
    target: '2C(s) + 3H₂(g) → C₂H₆(g)',
    targetSpecies: { 'C(s)': -2, 'H₂(g)': -3, 'C₂H₆(g)': 1 },
    steps: [C_COMBUSTION, H2_COMBUSTION, ETHANE_COMBUSTION],
    solution: [2, 3, -1],
    referenceDeltaHkJ: -84.0,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'sulfur-trioxide',
    name: 'Oxidation of sulfur dioxide',
    target: 'SO₂(g) + ½O₂(g) → SO₃(g)',
    targetSpecies: { 'SO₂(g)': -1, 'O₂(g)': -0.5, 'SO₃(g)': 1 },
    steps: [
      {
        equation: 'S(s) + O₂(g) → SO₂(g)',
        species: { 'S(s)': -1, 'O₂(g)': -1, 'SO₂(g)': 1 },
        deltaHkJ: -296.8,
      },
      {
        equation: 'S(s) + 1½O₂(g) → SO₃(g)',
        species: { 'S(s)': -1, 'O₂(g)': -1.5, 'SO₃(g)': 1 },
        deltaHkJ: -395.7,
      },
    ],
    solution: [-1, 1],
    referenceDeltaHkJ: -98.9,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'nitrogen-dioxide',
    name: 'Oxidation of nitrogen monoxide',
    target: 'NO(g) + ½O₂(g) → NO₂(g)',
    targetSpecies: { 'NO(g)': -1, 'O₂(g)': -0.5, 'NO₂(g)': 1 },
    steps: [
      {
        equation: '½N₂(g) + ½O₂(g) → NO(g)',
        species: { 'N₂(g)': -0.5, 'O₂(g)': -0.5, 'NO(g)': 1 },
        deltaHkJ: 90.3,
      },
      {
        equation: '½N₂(g) + O₂(g) → NO₂(g)',
        species: { 'N₂(g)': -0.5, 'O₂(g)': -1, 'NO₂(g)': 1 },
        deltaHkJ: 33.2,
      },
    ],
    solution: [-1, 1],
    referenceDeltaHkJ: -57.1,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
  {
    id: 'diamond-graphite',
    name: 'Diamond to graphite',
    target: 'C(s, diamond) → C(s, graphite)',
    targetSpecies: { 'C(s, diamond)': -1, 'C(s, graphite)': 1 },
    steps: [
      {
        equation: 'C(s, graphite) + O₂(g) → CO₂(g)',
        species: { 'C(s, graphite)': -1, 'O₂(g)': -1, 'CO₂(g)': 1 },
        deltaHkJ: -393.5,
      },
      {
        equation: 'C(s, diamond) + O₂(g) → CO₂(g)',
        species: { 'C(s, diamond)': -1, 'O₂(g)': -1, 'CO₂(g)': 1 },
        deltaHkJ: -395.4,
      },
    ],
    solution: [-1, 1],
    referenceDeltaHkJ: -1.9,
    ...reviewed([ENERGY_SOURCES.nist]),
  },
];

export const HESS_MULTIPLIERS = [-4, -3, -2, -1, -0.5, 0, 0.5, 1, 2, 3, 4] as const;

const SPECIES_EPSILON = 1e-9;

export interface HessCombination {
  net: SpeciesVector;
  deltaHkJ: number;
  matchesTarget: boolean;
  /** Species whose net coefficient differs from the target (target minus current). */
  mismatches: { species: string; have: number; need: number }[];
}

export function combineHess(set: HessSet, multipliers: readonly number[]): HessCombination | null {
  if (multipliers.length !== set.steps.length) return null;
  if (multipliers.some((value) => !(HESS_MULTIPLIERS as readonly number[]).includes(value))) {
    return null;
  }
  const net: SpeciesVector = {};
  let deltaHkJ = 0;
  set.steps.forEach((step, index) => {
    const factor = multipliers[index];
    deltaHkJ += factor * step.deltaHkJ;
    Object.entries(step.species).forEach(([species, coefficient]) => {
      net[species] = (net[species] ?? 0) + factor * coefficient;
    });
  });
  const allSpecies = new Set([...Object.keys(net), ...Object.keys(set.targetSpecies)]);
  const mismatches = [...allSpecies]
    .map((species) => ({
      species,
      have: roundTo(net[species] ?? 0, 6),
      need: set.targetSpecies[species] ?? 0,
    }))
    .filter((entry) => Math.abs(entry.have - entry.need) > SPECIES_EPSILON);
  return {
    net,
    deltaHkJ: roundTo(deltaHkJ, 1),
    matchesTarget: mismatches.length === 0,
    mismatches,
  };
}

/** Writes a species vector as an equation, e.g. "C(s) + 2H₂(g) → CH₄(g)". */
export function formatSpeciesVector(vector: SpeciesVector): string {
  const side = (sign: 1 | -1) =>
    Object.entries(vector)
      .filter(([, coefficient]) => coefficient * sign > SPECIES_EPSILON)
      .map(([species, coefficient]) => `${formatCoefficient(Math.abs(coefficient))}${species}`)
      .join(' + ') || '∅';
  return `${side(-1)} → ${side(1)}`;
}

export function formatCoefficient(value: number): string {
  if (Math.abs(value - 1) < SPECIES_EPSILON) return '';
  const whole = Math.floor(value + SPECIES_EPSILON);
  const half = Math.abs(value - whole - 0.5) < SPECIES_EPSILON;
  if (half) return whole === 0 ? '½' : `${whole}½`;
  return `${roundTo(value, 3)}`;
}

export function describeMultiplier(value: number): string {
  if (value === 0) return 'Not used';
  const reverse = value < 0 ? 'Reverse' : 'Keep';
  const size = Math.abs(value);
  return size === 1 ? reverse : `${reverse}, × ${formatCoefficient(size) || '1'}`;
}

// ---------------------------------------------------------------------------
// Senior: fuels
// ---------------------------------------------------------------------------

export interface Fuel {
  id: string;
  name: string;
  formula: string;
  state: 'g' | 'l';
  molarMass: number;
  /** Standard enthalpy of combustion, kJ mol⁻¹, water as liquid. */
  combustionkJPerMol: number;
  sources: DataSource[];
  reviewStatus: ReviewStatus;
}

function fuel(
  id: string,
  name: string,
  formula: string,
  state: 'g' | 'l',
  molarMass: number,
  combustionkJPerMol: number,
): Fuel {
  return {
    id,
    name,
    formula,
    state,
    molarMass,
    combustionkJPerMol,
    ...reviewed([ENERGY_SOURCES.nist]),
  };
}

export const FUELS: Fuel[] = [
  fuel('hydrogen', 'Hydrogen', 'H₂', 'g', 2.016, -285.8),
  fuel('methane', 'Methane', 'CH₄', 'g', 16.04, -890),
  fuel('ethane', 'Ethane', 'C₂H₆', 'g', 30.07, -1560),
  fuel('propane', 'Propane', 'C₃H₈', 'g', 44.1, -2219),
  fuel('butane', 'Butane', 'C₄H₁₀', 'g', 58.12, -2877),
  fuel('octane', 'Octane', 'C₈H₁₈', 'l', 114.23, -5470),
  fuel('methanol', 'Methanol', 'CH₃OH', 'l', 32.04, -726),
  fuel('ethanol', 'Ethanol', 'C₂H₅OH', 'l', 46.07, -1367),
];

export type FuelBasis = 'per-mol' | 'per-gram';

export function energyPerGram(entry: Fuel): number {
  return Math.abs(entry.combustionkJPerMol) / entry.molarMass;
}

export function fuelValue(entry: Fuel, basis: FuelBasis): number {
  return basis === 'per-mol' ? Math.abs(entry.combustionkJPerMol) : energyPerGram(entry);
}

export function rankFuels(basis: FuelBasis): Fuel[] {
  return [...FUELS].sort((a, b) => fuelValue(b, basis) - fuelValue(a, basis));
}

export function checkFuelPrediction(fuelId: string): AnswerCheck {
  const chosen = FUELS.find((entry) => entry.id === fuelId);
  if (!chosen) return { verdict: 'invalid', feedback: 'Choose one of the listed fuels.' };
  const best = rankFuels('per-gram')[0];
  if (chosen.id === best.id) {
    return {
      verdict: 'correct',
      feedback: `Correct. ${best.name} releases ${formatSig(energyPerGram(best), 3)} kJ g⁻¹. Each molecule is light (M = ${best.molarMass} g mol⁻¹), so one gram contains many moles, even though it has the smallest ΔcH per mole here.`,
    };
  }
  const topPerMol = rankFuels('per-mol')[0];
  const perMolTrap = chosen.id === topPerMol.id;
  return {
    verdict: 'other',
    feedback: perMolTrap
      ? `${chosen.name} has the largest ΔcH per mole (${Math.abs(chosen.combustionkJPerMol)} kJ mol⁻¹), but its molecules are heavy. Per gram it gives ${formatSig(energyPerGram(chosen), 3)} kJ g⁻¹, less than ${best.name} at ${formatSig(energyPerGram(best), 3)} kJ g⁻¹.`
      : `${chosen.name} gives ${formatSig(energyPerGram(chosen), 3)} kJ g⁻¹. Divide |ΔcH| by M for each fuel: ${best.name} gives the most, ${formatSig(energyPerGram(best), 3)} kJ g⁻¹.`,
  };
}

export interface ActivationEnergyRange {
  min: number;
  max: number;
  step: number;
  initial: number;
}

const EA_RANGE_FRACTIONS = { min: 0.1, max: 1.5, initial: 0.6, steps: 50 } as const;

/** Slider range for an illustrative Ea, always above an endothermic product level. */
export function activationEnergyRange(deltaHkJ: number): ActivationEnergyRange {
  const size = Math.abs(deltaHkJ);
  const floor = Math.max(0, deltaHkJ);
  const step = Math.max(1, Math.round(size / EA_RANGE_FRACTIONS.steps));
  const snap = (value: number) => Math.round(value / step) * step;
  return {
    min: snap(floor + size * EA_RANGE_FRACTIONS.min),
    max: snap(floor + size * EA_RANGE_FRACTIONS.max),
    step,
    initial: snap(floor + size * EA_RANGE_FRACTIONS.initial),
  };
}

function sideText(coefficient: number): string {
  if (Math.abs(coefficient) < SPECIES_EPSILON) return 'none';
  const side = coefficient < 0 ? 'reactant' : 'product';
  return `${formatCoefficient(Math.abs(coefficient)) || '1'} on the ${side} side`;
}

/** Plain-language feedback for one species that does not match the target. */
export function describeMismatch(mismatch: { species: string; have: number; need: number }) {
  if (mismatch.need === 0) {
    return `${mismatch.species} is not in the target, but your sum leaves ${sideText(mismatch.have)}. Make it cancel.`;
  }
  return `${mismatch.species}: the target needs ${sideText(mismatch.need)}; your sum gives ${sideText(mismatch.have)}.`;
}
